import { useEffect, useRef } from "react";
import * as pc from "playcanvas";
import { useInteractiveAppearance } from "hooks/playcanvas/interactive-appearance";
import { getBoundingBox } from "3d/entity-utils";
var timeBetweenUpdates = 0.2; // 200ms
export var Node3D = function (_a) {
    var app = _a.app, node = _a.node, nodeEntity = _a.nodeEntity, materialAssets = _a.materialAssets, hoverId = _a.hoverId, position = _a.position, onPositionChange = _a.onPositionChange, onBoundingBoxChange = _a.onBoundingBoxChange, isSelected = _a.isSelected, isHovered = _a.isHovered, hasWarnings = _a.hasWarnings, overlayData = _a.overlayData, isMultiSelecting = _a.isMultiSelecting, isZoomedIn = _a.isZoomedIn;
    var timeSinceLastBoundingBoxUpdate = useRef(0);
    var timeSinceLastPositionUpdate = useRef(0);
    useEffect(function () {
        nodeEntity.setPosition(new pc.Vec3(node.position.x, node.position.y, node.position.z));
    }, [nodeEntity, node.position]);
    /* Grip position rotation changed */
    useEffect(function () {
        nodeEntity.setLocalEulerAngles(0, node.rotationY, 0);
    }, [node.rotationY, nodeEntity]);
    // update position when changed
    useEffect(function () {
        var cameraEntity = app.root.findByName("Camera");
        var camera = cameraEntity === null || cameraEntity === void 0 ? void 0 : cameraEntity.camera;
        var updatePosition = function (dt) {
            timeSinceLastPositionUpdate.current += dt;
            if (timeSinceLastPositionUpdate.current < timeBetweenUpdates) {
                return;
            }
            else {
                timeSinceLastPositionUpdate.current = 0;
            }
            if (!camera)
                return;
            var nodeEntityPosition = camera.worldToScreen(nodeEntity.getPosition());
            // only cause a rerender when the value has actually changed
            if ((position === null || position === void 0 ? void 0 : position.x) !== nodeEntityPosition.x ||
                (position === null || position === void 0 ? void 0 : position.y) !== nodeEntityPosition.y) {
                onPositionChange({ x: nodeEntityPosition.x, y: nodeEntityPosition.y });
            }
        };
        app.on("update", updatePosition);
        return function () {
            app.off("update", updatePosition);
        };
    }, [nodeEntity, app, position === null || position === void 0 ? void 0 : position.x, position === null || position === void 0 ? void 0 : position.y, onPositionChange]);
    useEffect(function () {
        /* We only care about the bounding box when the node is hovered or selected or have warnings,
         * or if overlay data should be shown */
        if (isMultiSelecting ||
            (!isHovered && !isSelected && !hasWarnings && !overlayData)) {
            return;
        }
        var updateBoundingBox = function (dt) {
            timeSinceLastBoundingBoxUpdate.current += dt;
            if (timeSinceLastBoundingBoxUpdate.current < timeBetweenUpdates) {
                return;
            }
            else {
                timeSinceLastBoundingBoxUpdate.current = 0;
            }
            var cameraEntity = app.root.findByName("Camera");
            var camera = cameraEntity === null || cameraEntity === void 0 ? void 0 : cameraEntity.camera;
            if (!camera)
                return;
            var bbox = getBoundingBox(nodeEntity);
            if (bbox) {
                var bboxBottomLeft = camera.worldToScreen(new pc.Vec3(bbox.center.x - bbox.halfExtents.x, bbox.center.y, bbox.center.z + bbox.halfExtents.z));
                var bboxTopRight = camera.worldToScreen(new pc.Vec3(bbox.center.x + bbox.halfExtents.x, bbox.center.y, bbox.center.z - bbox.halfExtents.z));
                var boundingBoxWidth = Math.round(bboxBottomLeft.x - bboxTopRight.x);
                var boundingBoxHeight = Math.round(bboxBottomLeft.y - bboxTopRight.y);
                onBoundingBoxChange({
                    height: boundingBoxHeight,
                    width: boundingBoxWidth,
                    center: {
                        x: Math.round(bboxBottomLeft.x - boundingBoxWidth / 2),
                        y: Math.round(bboxBottomLeft.y - boundingBoxHeight / 2),
                    },
                });
            }
        };
        app.on("update", updateBoundingBox);
        return function () {
            app.off("update", updateBoundingBox);
        };
    }, [
        nodeEntity,
        isSelected,
        isHovered,
        node.id,
        node.type,
        app,
        isZoomedIn,
        hasWarnings,
        overlayData,
        isMultiSelecting,
        onBoundingBoxChange,
    ]);
    useInteractiveAppearance(node.id, nodeEntity, materialAssets, hoverId);
    return null;
};
